import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'

// One hook for both sides of the conversation. RLS scopes the rows: a
// superadmin sees every ticket, an org member sees only their org's. The
// message-insert trigger maintains last_message_at, unread flags and status.

function tTicket(r) {
  return {
    id: r.id,
    organizationId: r.organization_id,
    openedBy: r.opened_by,
    subject: r.subject,
    status: r.status,
    priority: r.priority,
    adminUnread: r.admin_unread,
    userUnread: r.user_unread,
    createdAt: r.created_at ? new Date(r.created_at) : null,
    lastMessageAt: r.last_message_at ? new Date(r.last_message_at) : null,
  }
}

function tMsg(r) {
  return {
    id: r.id,
    ticketId: r.ticket_id,
    senderId: r.sender_id,
    senderName: r.sender_name,
    senderRole: r.sender_role,
    body: r.body,
    createdAt: r.created_at ? new Date(r.created_at) : null,
  }
}

// Org members raise tickets; superadmins answer them from HQ → Inbox, so they
// are not the ones opening tickets.
export const canContactSupport = (user) =>
  !!user && user.role !== 'superadmin' && !!user.organizationId

export function useSupport(currentUser) {
  const isSuper = currentUser?.role === 'superadmin'
  const [tickets, setTickets] = useState([])
  const [messages, setMessages] = useState({}) // ticketId -> [msg]

  const fetchTickets = useCallback(async () => {
    let q = supabase.from('support_tickets').select('*')
    if (!isSuper && currentUser?.id) q = q.eq('opened_by', currentUser.id)
    const { data } = await q.order('last_message_at', { ascending: false })
    setTickets((data || []).map(tTicket))
  }, [isSuper, currentUser?.id])

  useEffect(() => { if (currentUser?.id) fetchTickets() }, [currentUser?.id, fetchTickets])

  // Tickets are fetched once on mount — there is no polling or realtime here.
  // That is fine while support lives on a page you deliberately open, but the
  // widget's badge is supposed to tell you the team replied, so refresh when
  // the tab regains focus. Callers also refetch when they open the panel.
  useEffect(() => {
    if (!currentUser?.id) return undefined
    function onFocus() { fetchTickets() }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [currentUser?.id, fetchTickets])

  async function fetchMessages(ticketId) {
    const { data } = await supabase
      .from('support_messages').select('*')
      .eq('ticket_id', ticketId).order('created_at')
    setMessages((m) => ({ ...m, [ticketId]: (data || []).map(tMsg) }))
  }

  async function createTicket({ subject, body, priority = 'normal' }) {
    const { data: t, error } = await supabase.from('support_tickets').insert({
      organization_id: currentUser.organizationId,
      opened_by: currentUser.id,
      subject, priority,
    }).select().single()
    if (error) return { success: false, message: 'Could not open ticket: ' + error.message }
    const { error: mErr } = await supabase.from('support_messages').insert({
      ticket_id: t.id, sender_id: currentUser.id, sender_role: 'customer',
      sender_name: currentUser.name || '', body,
    })
    if (mErr) return { success: false, message: 'Ticket opened, but the message failed: ' + mErr.message }
    // Email the PetroTrack team about the new ticket — fire-and-forget, and a
    // silent no-op server-side until Resend is configured.
    supabase.functions.invoke('notify-ticket', { body: { ticketId: t.id } }).catch(() => {})
    await fetchTickets()
    return { success: true, ticketId: t.id }
  }

  async function sendMessage(ticketId, body) {
    const { error } = await supabase.from('support_messages').insert({
      ticket_id: ticketId, sender_id: currentUser.id,
      sender_role: isSuper ? 'support' : 'customer',
      sender_name: currentUser.name || '', body,
    })
    if (error) return { success: false, message: 'Reply failed: ' + error.message }
    await Promise.all([fetchMessages(ticketId), fetchTickets()])
    return { success: true }
  }

  async function setStatus(ticketId, status) {
    const { error } = await supabase.from('support_tickets').update({ status }).eq('id', ticketId)
    if (error) return { success: false, message: error.message }
    await fetchTickets()
    return { success: true }
  }

  // Clear my side's unread flag (optimistic — no refetch needed).
  async function markRead(ticketId) {
    const patch = isSuper ? { admin_unread: false } : { user_unread: false }
    await supabase.from('support_tickets').update(patch).eq('id', ticketId)
    setTickets((ts) => ts.map((t) =>
      t.id === ticketId ? { ...t, [isSuper ? 'adminUnread' : 'userUnread']: false } : t
    ))
  }

  const unreadCount = tickets.filter(
    (t) => (isSuper ? t.adminUnread : t.userUnread) && t.status !== 'resolved'
  ).length

  return { tickets, messages, fetchTickets, fetchMessages, createTicket, sendMessage, setStatus, markRead, unreadCount }
}
